import { useState, useEffect, useCallback } from 'react';
import {
  PhoneIncoming, PhoneOutgoing, PhoneMissed, MessageSquare, Mail,
  Send, Phone, RefreshCw, Radio, Clock, MessagesSquare,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useVoice } from '@/context/VoiceContext';
import RecordingPlayer from '@/components/voice/RecordingPlayer';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'call', label: 'Calls' },
  { id: 'sms', label: 'Texts' },
  { id: 'email', label: 'Emails' },
];

function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
}

function fmtDuration(secs) {
  if (!secs) return null;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m ? `${m}m ${s}s` : `${s}s`;
}

function KindIcon({ item }) {
  const outbound = item.direction === 'outbound';
  if (item.kind === 'call') {
    const missed = ['no-answer', 'missed', 'canceled', 'busy', 'failed'].includes(item.status) && !outbound;
    if (missed) return <span className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0"><PhoneMissed className="w-4 h-4 text-red-500" /></span>;
    return outbound
      ? <span className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0"><PhoneOutgoing className="w-4 h-4 text-blue-500" /></span>
      : <span className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0"><PhoneIncoming className="w-4 h-4 text-emerald-500" /></span>;
  }
  if (item.kind === 'sms') {
    return <span className="w-8 h-8 rounded-full bg-violet-50 flex items-center justify-center flex-shrink-0"><MessageSquare className="w-4 h-4 text-violet-500" /></span>;
  }
  return <span className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center flex-shrink-0"><Mail className="w-4 h-4 text-amber-600" /></span>;
}

export default function CommunicationsTimeline({ contactId, contactName, phone }) {
  const { requestDial } = useVoice();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ calls: 0, sms: 0, emails: 0 });
  const [primaryPhone, setPrimaryPhone] = useState('');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [recordingOn, setRecordingOn] = useState(null);
  const [smsBody, setSmsBody] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/voice/communications', { params: { contact_id: contactId, limit: 300 } });
      setItems(r.data?.items || []);
      setCounts(r.data?.counts || { calls: 0, sms: 0, emails: 0 });
      setPrimaryPhone(r.data?.primary_phone || '');
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/voice/recording-settings')
      .then(r => setRecordingOn(!!r.data?.enabled))
      .catch(() => setRecordingOn(null));
  }, []);

  const target = primaryPhone || phone || '';

  const handleCall = () => {
    if (!target) { toast.error('This contact has no phone number'); return; }
    requestDial(target, contactName || target);
  };

  const handleSendSms = async (e) => {
    e.preventDefault();
    const body = smsBody.trim();
    if (!body) return;
    if (!target) { toast.error('This contact has no phone number'); return; }
    setSending(true);
    try {
      await api.post('/voice/sms', { to: target, body, contact_id: contactId });
      setSmsBody('');
      toast.success('Text sent');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send the text');
    } finally {
      setSending(false);
    }
  };

  const filtered = filter === 'all' ? items : items.filter(i => i.kind === filter);

  return (
    <div className="space-y-4" data-testid="communications-timeline">
      {/* Action bar */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          data-testid="comm-call-btn"
          onClick={handleCall}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition-colors"
        >
          <Phone className="w-3.5 h-3.5" /> Call
        </button>
        <form onSubmit={handleSendSms} className="flex items-center gap-2 flex-1 min-w-[240px]">
          <input
            data-testid="comm-sms-input"
            value={smsBody}
            onChange={e => setSmsBody(e.target.value)}
            placeholder={target ? `Text ${target}...` : 'No phone number on file'}
            disabled={!target}
            className="flex-1 text-xs px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:border-[#003366]/40 disabled:bg-slate-50"
          />
          <button
            data-testid="comm-sms-send-btn"
            type="submit"
            disabled={sending || !smsBody.trim() || !target}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#003366] text-white text-xs font-semibold hover:bg-[#004080] disabled:bg-slate-300 transition-colors"
          >
            <Send className="w-3.5 h-3.5" /> Send
          </button>
        </form>
        <button
          data-testid="comm-refresh-btn"
          onClick={load}
          className="p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
          title="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Filters + recording notice */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-0.5 bg-slate-100 rounded-lg p-0.5">
          {FILTERS.map(f => (
            <button
              key={f.id}
              data-testid={`comm-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 text-[11px] font-semibold rounded-md transition-all ${filter === f.id ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              {f.label}
              {f.id === 'call' && counts.calls ? ` (${counts.calls})` : ''}
              {f.id === 'sms' && counts.sms ? ` (${counts.sms})` : ''}
              {f.id === 'email' && counts.emails ? ` (${counts.emails})` : ''}
            </button>
          ))}
        </div>
        {recordingOn !== null && (
          <span
            data-testid="comm-recording-status"
            className={`inline-flex items-center gap-1.5 text-[10px] font-semibold px-2.5 py-1 rounded-full border ${recordingOn ? 'bg-red-50 text-red-600 border-red-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}
          >
            <Radio className="w-3 h-3" /> {recordingOn ? 'Call recording ON' : 'Call recording OFF'}
          </span>
        )}
      </div>

      {/* Timeline */}
      {loading && items.length === 0 ? (
        <div className="py-10 text-center text-sm text-slate-400">Loading history...</div>
      ) : filtered.length === 0 ? (
        <div className="py-10 text-center text-slate-400">
          <MessagesSquare className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No communications logged yet</p>
          <p className="text-xs mt-1">Calls, texts and emails with this contact will appear here.</p>
        </div>
      ) : (
        <div className="relative pl-3">
          <div className="absolute left-[19px] top-2 bottom-2 w-px bg-slate-100" />
          <ul className="space-y-2.5">
            {filtered.map(item => (
              <li
                key={`${item.kind}-${item.id}`}
                data-testid={`comm-item-${item.kind}-${item.id}`}
                className="relative flex gap-3 bg-white border border-slate-100 rounded-xl p-3 hover:border-slate-200 transition-colors"
              >
                <KindIcon item={item} />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-slate-800">
                      {item.kind === 'call' && `${item.direction === 'outbound' ? 'Outgoing' : 'Incoming'} call`}
                      {item.kind === 'sms' && `${item.direction === 'outbound' ? 'Text sent' : 'Text received'}`}
                      {item.kind === 'email' && `Email · ${item.doc_type || 'document'}`}
                    </span>
                    <span className="text-[11px] text-slate-400">{fmtDateTime(item.at)}</span>
                    {item.kind === 'call' && fmtDuration(item.duration) && (
                      <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
                        <Clock className="w-3 h-3" />{fmtDuration(item.duration)}
                      </span>
                    )}
                    {item.status && (
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${item.status === 'failed' ? 'bg-red-50 text-red-600' : 'bg-slate-100 text-slate-500'}`}>
                        {item.status}
                      </span>
                    )}
                  </div>

                  {item.number && (
                    <p className="text-[11px] font-mono text-slate-400 mt-0.5">{item.number}</p>
                  )}
                  {item.kind === 'sms' && item.body && (
                    <p className="text-sm text-slate-700 mt-1.5 whitespace-pre-wrap break-words">{item.body}</p>
                  )}
                  {item.kind === 'email' && (
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      To {item.to || '—'}{item.reason ? ` · ${item.reason}` : ''}
                    </p>
                  )}
                  {item.kind === 'call' && (
                    item.recording_sid ? (
                      <div className="mt-2 max-w-sm">
                        <RecordingPlayer callSid={item.call_sid} duration={item.recording_duration || item.duration} />
                      </div>
                    ) : (
                      <p className="text-[11px] text-slate-300 mt-1">No recording</p>
                    )
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
